import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Mail, Calendar, ShieldCheck, Search, FileText, Megaphone,
    CheckCircle2, AlertCircle, Clock3, ChevronLeft, ChevronRight, CheckCircle
} from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";
import { calculateOverallScore } from "../service/Operations";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

function fmt(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function StatusBadge({ status }) {
    if (!status) return null;
    const s = status.toLowerCase();
    const isComplete = s === "completed";
    const isError = s === "error";
    const cls = isComplete ? "badge-complete" : isError ? "badge-error" : "badge-processing";
    const Icon = isComplete ? CheckCircle : isError ? AlertCircle : Clock3;
    const label = isComplete ? "Complete" : isError ? "Error" : "In Process";
    return (
        <span className={`status-badge ${cls}`}>
            <Icon size={12} />
            {label}
        </span>
    );
}

export default function AdminUser() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("id");

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [user, setUser] = useState(null);
    const [manuscripts, setManuscripts] = useState([]);
    const [signup, setSignup] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        if (!userId) {
            navigate("/admin");
            return;
        }
        request("GET", `/admin/user/${userId}`)
            .then(res => {
                if (!res.success) {
                    navigate("/admin");
                    return;
                }
                setUser(res.user);
                setManuscripts(res.manuscripts ?? []);
                setSignup(res.signup ?? null);
                setLoading(false);
            })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"));
    }, [userId]);

    const filtered = manuscripts
        .filter(m => {
            const q = search.toLowerCase();
            return (
                (m.title ?? "").toLowerCase().includes(q) ||
                (m.name ?? "").toLowerCase().includes(q) ||
                (m.authors ?? "").toLowerCase().includes(q)
            );
        })
        .sort((a, b) => (b.uploadDate ?? 0) - (a.uploadDate ?? 0));

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const completedCount = manuscripts.filter(m => m.status?.toLowerCase() === "completed").length;
    const avgScore = (() => {
        const scored = manuscripts.filter(m => m.status?.toLowerCase() === "completed" && m.noveltyScore != null);
        if (scored.length === 0) return null;
        const total = scored.reduce((sum, m) => sum + calculateOverallScore(m), 0);
        return Math.round(total / scored.length);
    })();

    if (loading) {
        return (
            <div className="home-layout">
                <Sidebar activePage="Admin" />
                <main className="home-main">
                    <Header />
                    <div className="home-content">
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError || "Loading..."}</div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="home-layout">
            <Sidebar activePage="Admin" />
            <main className="home-main">
                <Header />
                <div className="home-content">
                    <div className="upload-main">

                    <button className="chpw-back" onClick={() => navigate("/admin")}>
                        <ArrowLeft size={14} />
                        Back to Admin
                    </button>

                    {/* User profile card */}
                    <div className="submissions-card" style={{ padding: "24px", marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
                            {user.imageUrl ? (
                                <img
                                    src={user.imageUrl.replaceAll('"', '')}
                                    alt=""
                                    style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                />
                            ) : (
                                <div style={{
                                    width: 64, height: 64, borderRadius: "50%",
                                    background: "var(--blue-bg)", color: "var(--blue)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 24, fontWeight: 600, flexShrink: 0,
                                }}>
                                    {(user.firstName?.[0] ?? "?").toUpperCase()}
                                </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--ink-900)" }}>
                                    {user.firstName} {user.lastName}
                                </h2>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", marginTop: 6 }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--ink-500)" }}>
                                        <Mail size={13} color="var(--ink-300)" />
                                        {user.email}
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--ink-500)" }}>
                                        <Calendar size={13} color="var(--ink-300)" />
                                        Joined {fmt(user.dateCreated)}
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--ink-500)" }}>
                                        <ShieldCheck size={13} color={user.role === "ADMIN" ? "#22c55e" : "var(--ink-300)"} />
                                        {user.role}
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--ink-500)" }}
                                          title={signup ? [
                                              signup.medium && `Medium: ${signup.medium}`,
                                              signup.content && `Content: ${signup.content}`,
                                              signup.term && `Term: ${signup.term}`,
                                              signup.referrerHost && `Referrer: ${signup.referrerHost}`,
                                              signup.landingPage && `Landed on: ${signup.landingPage}`,
                                          ].filter(Boolean).join("\n") || undefined : undefined}>
                                        <Megaphone size={13} color="var(--ink-300)" />
                                        {signup
                                            ? <>Signed up via <strong style={{ fontWeight: 600 }}>{signup.source}</strong>{signup.campaign ? ` · ${signup.campaign}` : ""}{signup.method === "google" ? " (Google)" : ""}</>
                                            : "Signed up before source tracking"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="admin-stats-row" style={{ marginBottom: 16 }}>
                        <div className="submissions-card" style={{ padding: "16px 20px", textAlign: "center" }}>
                            <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                                {manuscripts.length}
                            </p>
                            <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>Total Uploads</p>
                        </div>
                        <div className="submissions-card" style={{ padding: "16px 20px", textAlign: "center" }}>
                            <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                                {completedCount}
                            </p>
                            <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>Completed</p>
                        </div>
                        <div className="submissions-card" style={{ padding: "16px 20px", textAlign: "center" }}>
                            <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                                {avgScore ?? "—"}
                            </p>
                            <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>Avg Score</p>
                        </div>
                    </div>

                    {/* Submissions */}
                    <div className="submissions-filters">
                        <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>Submissions</p>
                        <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: "auto" }}>
                            <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                            <input
                                type="text"
                                placeholder="Search…"
                                value={search}
                                onChange={e => { setSearch(e.target.value); setPage(1); }}
                                style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                            />
                            {search && (
                                <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                            )}
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-bar" style={{ marginBottom: 8 }}>
                            <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</p>
                            <div className="pagination-controls">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                <div className="pagination-pages">
                                    {pageWindows(safePage, totalPages).map((n, i) =>
                                        n === null
                                            ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                            : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                    )}
                                </div>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                            </div>
                            <div className="pagination-spacer" />
                        </div>
                    )}

                    <div className="sub-list">
                        {filtered.length === 0 ? (
                            <div className="submissions-empty">
                                <FileText size={32} className="submissions-empty-icon" />
                                <p className="submissions-empty-title">No submissions found</p>
                            </div>
                        ) : paginated.map((m) => {
                            const score = calculateOverallScore(m);
                            const title = (m.title?.trim()) || m.name?.replace(/\.[^/.]+$/, "") || "Untitled";
                            const authors = m.authors?.trim() || "Unknown Authors";
                            const s = m.status?.toLowerCase();
                            const isComplete = s === "completed";
                            return (
                                <div key={m.id} className="sub-card" onClick={() => navigate(`/admin/submission?id=${m.id}`)} style={{ cursor: "pointer" }}>
                                    <div className="sub-icon">
                                        <FileText size={15} />
                                    </div>
                                    <div className="sub-info">
                                        <p className="sub-title">{title}</p>
                                        <p className="sub-authors">{authors}</p>
                                        {isComplete && (
                                            <div className="sub-scores-row">
                                                {m.noveltyScore != null && <span className="sub-score-chip sub-score-chip-n">N: {m.noveltyScore}</span>}
                                                {m.methodScore != null && <span className="sub-score-chip sub-score-chip-m">M: {m.methodScore}</span>}
                                                {m.impactScore != null && <span className="sub-score-chip sub-score-chip-i">I: {m.impactScore}</span>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="sub-meta">
                                        <p className="sub-date">{m.uploadDate ? new Date(m.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</p>
                                        <div className="sub-right">
                                            <StatusBadge status={m.status} />
                                            {isComplete && score > 0 && <span className="sub-score">{score}</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
